import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../api';

// Multi-select dropdown
function MultiSelectDropdown({ options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    if (id === 'all') {
      onChange(selected.length === options.length ? [] : options.map(o => o.id));
    } else {
      onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
    }
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === options.length
    ? 'All trailers'
    : selected.length === 1
    ? options.find(o => o.id === selected[0])?.name
    : `${selected.length} trailers selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '8px 10px', border: '1px solid var(--slate-200)',
          borderRadius: '6px', fontSize: '13px', background: 'white',
          color: selected.length ? 'var(--slate-800)' : 'var(--slate-400)',
          textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
        <span>{label}</span>
        <span style={{ fontSize: '10px', color: 'var(--slate-400)' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'white', border: '1px solid var(--slate-200)', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '220px', overflowY: 'auto', marginTop: '4px',
        }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
            cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid var(--slate-100)',
            color: 'var(--slate-700)',
          }}>
            <input type="checkbox" checked={selected.length === options.length}
              onChange={() => toggle('all')} />
            All trailers
          </label>
          {options.map(o => (
            <label key={o.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
              cursor: 'pointer', fontSize: '13px', color: 'var(--slate-700)',
              background: selected.includes(o.id) ? 'var(--slate-50)' : 'white',
            }}>
              <input type="checkbox" checked={selected.includes(o.id)}
                onChange={() => toggle(o.id)} />
              {o.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Address autocomplete using OpenStreetMap Nominatim
function AddressAutocomplete({ value, onChange, onSelect }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    clearTimeout(debounceRef.current);
    if (val.length < 4) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&limit=5&countrycodes=us`,
          { headers: { 'User-Agent': 'RentFlow-Fleet/1.0' } }
        );
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(true);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const handleSelect = (suggestion) => {
    const display = suggestion.display_name;
    setQuery(display);
    onChange(display);
    onSelect({
      address: display,
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon),
    });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder="Start typing an address..."
        style={{
          padding: '8px 10px', border: '1px solid var(--slate-200)',
          borderRadius: '6px', fontSize: '13px', width: '100%', background: 'white',
        }}
      />
      {loading && (
        <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--slate-400)' }}>
          Searching...
        </div>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'white', border: '1px solid var(--slate-200)', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', marginTop: '2px', maxHeight: '240px', overflowY: 'auto',
        }}>
          {suggestions.map((s, i) => (
            <button key={i} type="button" onClick={() => handleSelect(s)}
              style={{
                display: 'block', width: '100%', padding: '10px 12px', border: 'none',
                background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '13px',
                color: 'var(--slate-700)', borderBottom: i < suggestions.length - 1 ? '1px solid var(--slate-100)' : 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--slate-50)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              {s.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GeofencePanel({ subAccountId, trackers }) {
  const [fences, setFences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFence, setEditingFence] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testStatus, setTestStatus] = useState(null);
  const [form, setForm] = useState({
    name: '',
    address: '',
    lat: null,
    lng: null,
    radius_miles: '1',
    tracker_ids: [],
    ghl_webhook_url: '',
    notify_on_enter: true,
    notify_on_exit: true,
  });

  const gpsTrackers = trackers.filter(t => !t.manual);

  const loadFences = async () => {
    try {
      const data = await apiFetch(`/api/geofences?sub_account_id=${subAccountId}`);
      setFences(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFences(); }, [subAccountId]);

  const openNewForm = () => {
    setEditingFence(null);
    setTestStatus(null);
    setForm({
      name: '',
      address: '',
      lat: null,
      lng: null,
      radius_miles: '1',
      tracker_ids: gpsTrackers.map(t => t.id),
      ghl_webhook_url: '',
      notify_on_enter: true,
      notify_on_exit: true,
    });
    setShowForm(true);
  };

  const openEditForm = (fence) => {
    setEditingFence(fence);
    setTestStatus(null);
    let trackerIds = [];
    if (fence.applies_to === 'all') {
      trackerIds = gpsTrackers.map(t => t.id);
    } else if (fence.tracker_id) {
      trackerIds = [fence.tracker_id];
    }
    setForm({
      name: fence.name,
      address: fence.address || '',
      lat: parseFloat(fence.lat),
      lng: parseFloat(fence.lng),
      radius_miles: String(fence.radius_miles),
      tracker_ids: trackerIds,
      ghl_webhook_url: fence.ghl_webhook_url,
      notify_on_enter: fence.notify_on_enter,
      notify_on_exit: fence.notify_on_exit,
    });
    setShowForm(true);
  };

  const handleAddressSelect = ({ address, lat, lng }) => {
    setForm(f => ({ ...f, address, lat, lng }));
  };

  const handleSendTest = async () => {
    if (!form.ghl_webhook_url) { alert('Please enter a webhook URL first.'); return; }
    setTestStatus('sending');
    try {
      const res = await fetch(form.ghl_webhook_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'geofence:entered',
          geofence_name: form.name || 'Test Zone',
          geofence_address: form.address || '123 Test St',
          tracker_id: 'test-tracker',
          tracker_name: gpsTrackers[0]?.name || 'Test Trailer',
          direction: 'entered',
          distance_miles: 0.1,
          latitude: form.lat || 29.4241,
          longitude: form.lng || -98.4936,
          renter_name: 'Test Renter',
          contact_id: 'test-contact',
          toll_detected: 'Yes',
          toll_road_name: 'Sam Houston Tollway',
          timestamp: new Date().toISOString(),
        }),
      });
      setTestStatus(res.ok ? 'success' : 'error');
    } catch (err) {
      setTestStatus('error');
    }
    setTimeout(() => setTestStatus(null), 4000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.address) { alert('Please enter and select an address from the suggestions.'); return; }
    if (!form.lat || !form.lng) { alert('Please select an address from the dropdown suggestions.'); return; }
    if (!form.ghl_webhook_url) { alert('Please enter a webhook URL.'); return; }
    if (form.tracker_ids.length === 0) { alert('Please select at least one trailer.'); return; }

    setSaving(true);
    try {
      const allSelected = form.tracker_ids.length === gpsTrackers.length;

      const fencesToSave = allSelected
        ? [{ applies_to: 'all', tracker_id: null, tracker_name: null }]
        : form.tracker_ids.map(tid => {
            const t = gpsTrackers.find(t => t.id === tid);
            return { applies_to: 'specific', tracker_id: tid, tracker_name: t?.name || tid };
          });

      if (editingFence) {
        const entry = fencesToSave[0];
        await apiFetch(`/api/geofences/${editingFence.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            sub_account_id: subAccountId,
            name: form.name,
            address: form.address,
            lat: form.lat,
            lng: form.lng,
            radius_miles: parseFloat(form.radius_miles),
            applies_to: entry.applies_to,
            tracker_id: entry.tracker_id,
            tracker_name: entry.tracker_name,
            ghl_webhook_url: form.ghl_webhook_url,
            notify_on_enter: form.notify_on_enter,
            notify_on_exit: form.notify_on_exit,
          }),
        });
      } else {
        await Promise.all(fencesToSave.map(entry =>
          apiFetch('/api/geofences', {
            method: 'POST',
            body: JSON.stringify({
              sub_account_id: subAccountId,
              name: form.name,
              address: form.address,
              lat: form.lat,
              lng: form.lng,
              radius_miles: parseFloat(form.radius_miles),
              applies_to: entry.applies_to,
              tracker_id: entry.tracker_id,
              tracker_name: entry.tracker_name,
              ghl_webhook_url: form.ghl_webhook_url,
              notify_on_enter: form.notify_on_enter,
              notify_on_exit: form.notify_on_exit,
            }),
          })
        ));
      }

      setShowForm(false);
      await loadFences();
    } catch (err) {
      alert('Failed to save geofence: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this geofence?')) return;
    await apiFetch(`/api/geofences/${id}?sub_account_id=${subAccountId}`, { method: 'DELETE' }).catch(() => {});
    await loadFences();
  };

  const inputStyle = {
    padding: '8px 10px', border: '1px solid var(--slate-200)',
    borderRadius: '6px', fontSize: '13px', width: '100%',
    background: 'white', color: 'var(--slate-800)',
  };
  const labelStyle = {
    fontSize: '12px', fontWeight: '600', color: 'var(--slate-600)',
    marginBottom: '5px', display: 'block',
  };

  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;

  return (
    <div className="alerts-panel">
      <div className="alerts-header">
        <div>
          <h2>Geofence Alerts</h2>
          <p>Get notified via RentFlow when a trailer enters or exits a defined area.</p>
        </div>
        <button className="btn-primary" onClick={openNewForm}>+ Add Geofence</button>
      </div>

      {showForm && (
        <div className="alert-form-card">
          <h3>{editingFence ? 'Edit Geofence' : 'New Geofence'}</h3>
          <form onSubmit={handleSave}>

            <div className="form-row">
              <div className="form-group">
                <label style={labelStyle}>Geofence Name</label>
                <input type="text" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Home Base, Yard, Customer Site"
                  style={inputStyle} required />
              </div>
              <div className="form-group">
                <label style={labelStyle}>Radius (miles)</label>
                <input type="number" min="0.1" step="0.1" value={form.radius_miles}
                  onChange={e => setForm(f => ({ ...f, radius_miles: e.target.value }))}
                  style={inputStyle} required />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Address</label>
              <AddressAutocomplete
                value={form.address}
                onChange={(val) => setForm(f => ({ ...f, address: val, lat: null, lng: null }))}
                onSelect={handleAddressSelect}
              />
              {form.lat && form.lng && (
                <span style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px', display: 'block' }}>
                  ✓ Location confirmed — {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
                </span>
              )}
              {!form.lat && form.address && (
                <span style={{ fontSize: '11px', color: '#f97316', marginTop: '4px', display: 'block' }}>
                  Select an address from the suggestions to confirm location
                </span>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Trailers</label>
              <MultiSelectDropdown
                options={gpsTrackers.map(t => ({ id: t.id, name: t.name }))}
                selected={form.tracker_ids}
                onChange={(ids) => setForm(f => ({ ...f, tracker_ids: ids }))}
                placeholder="Select trailers..."
              />
            </div>

            <div className="form-group" style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Webhook URL</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <input type="url" value={form.ghl_webhook_url}
                  onChange={e => { setForm(f => ({ ...f, ghl_webhook_url: e.target.value })); setTestStatus(null); }}
                  placeholder="https://services.leadconnectorhq.com/hooks/..."
                  style={{ ...inputStyle, flex: 1 }} required />
                <button
                  type="button"
                  onClick={handleSendTest}
                  disabled={!form.ghl_webhook_url || testStatus === 'sending'}
                  style={{
                    padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                    border: '1px solid var(--slate-200)', cursor: 'pointer', whiteSpace: 'nowrap',
                    background: testStatus === 'success' ? '#f0fdf4' : testStatus === 'error' ? '#fef2f2' : 'white',
                    color: testStatus === 'success' ? '#16a34a' : testStatus === 'error' ? '#dc2626' : 'var(--slate-600)',
                  }}>
                  {testStatus === 'sending' ? 'Sending...' : testStatus === 'success' ? '✓ Sent!' : testStatus === 'error' ? '✗ Failed' : 'Send Test'}
                </button>
              </div>
              <span style={{ fontSize: '11px', color: 'var(--slate-400)', marginTop: '4px', display: 'block' }}>
                Contact RentFlow support to receive your webhook URL. Use <strong>Send Test</strong> to map fields in your workflow before saving.
              </span>
            </div>

            <div className="form-group" style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Notify when</label>
              <div style={{ display: 'flex', gap: '16px', marginTop: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal', fontSize: '13px' }}>
                  <input type="checkbox" checked={form.notify_on_enter}
                    onChange={e => setForm(f => ({ ...f, notify_on_enter: e.target.checked }))} />
                  Trailer enters zone
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal', fontSize: '13px' }}>
                  <input type="checkbox" checked={form.notify_on_exit}
                    onChange={e => setForm(f => ({ ...f, notify_on_exit: e.target.checked }))} />
                  Trailer exits zone
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingFence ? 'Save Geofence' : `Create ${!form.tracker_ids.length || form.tracker_ids.length === gpsTrackers.length ? '' : form.tracker_ids.length + ' '}Geofence${form.tracker_ids.length > 1 && form.tracker_ids.length !== gpsTrackers.length ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      )}

      {fences.length === 0 && !showForm ? (
        <div className="empty-state">
          <span className="empty-icon">📍</span>
          <h3>No geofences set up yet</h3>
          <p>Add a geofence to get notified when a trailer enters or exits a location.</p>
          <button className="btn-primary" onClick={openNewForm}>Add Your First Geofence</button>
        </div>
      ) : (
        <div className="alerts-list">
          {fences.map(fence => (
            <div key={fence.id} className="alert-row">
              <div className="alert-row-main">
                <div className="alert-tracker-name">
                  {fence.name}
                  <span className="alert-label-badge" style={{ background: '#f0fdf4', color: '#166534' }}>
                    {fence.applies_to === 'all' ? 'All trailers' : fence.tracker_name}
                  </span>
                  <span className="alert-label-badge">
                    {fence.radius_miles} mi radius
                  </span>
                </div>
                <div className="alert-meta" style={{ marginTop: '4px' }}>
                  {fence.address}
                </div>
                <div className="alert-meta" style={{ marginTop: '4px' }}>
                  Notify on:
                  {fence.notify_on_enter && <strong> Enter</strong>}
                  {fence.notify_on_enter && fence.notify_on_exit && ' & '}
                  {fence.notify_on_exit && <strong>Exit</strong>}
                </div>
              </div>
              <div className="alert-row-actions">
                <button className="btn-icon" onClick={() => openEditForm(fence)} title="Edit">✏️</button>
                <button className="btn-icon danger" onClick={() => handleDelete(fence.id)} title="Delete">🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
